import React from 'react';
import { View } from 'react-native';
import { createStyleSheet, useStyles } from 'react-native-unistyles';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircleCheck, IconDefinition } from '@fortawesome/pro-regular-svg-icons';
import { translate } from '../services/translate';
import { WindowInformation } from '../services/window-information';
import { getPasswordRequirements } from '../services/password-requirements';
import PasswordInput from './PasswordInput';
import MyAppText from './MyAppText';
import WarningMessage from './WarningMessage';

interface Props {
    oldPassword?: string;
    password: string;
    confirmPassword: string;
    onChangeOldPassword?: (value: string) => void;
    onChangePassword: (value: string) => void;
    onChangeConfirmPassword: (value: string) => void;
    showOldPassword?: boolean;
    stacked?: boolean;
}

export default function PasswordForm({
    oldPassword = '',
    password,
    confirmPassword,
    onChangeOldPassword,
    onChangePassword,
    onChangeConfirmPassword,
    showOldPassword = true,
    stacked = false,
}: Props) {
    const { styles } = useStyles(styleSheet);
    const windowInfo = WindowInformation();
    const requirements = getPasswordRequirements(password);

    const inputStyle = stacked ? undefined : { maxWidth: 250 };
    const newPasswordsDisabled = showOldPassword && !oldPassword;

    const requirementItems = [
        { label: translate('atLeast14Characters'), valid: requirements.hasMinLength },
        { label: translate('atLeast2Digits'), valid: requirements.hasDigits },
        { label: translate('atLeastOneUppercase'), valid: requirements.hasUppercase },
        { label: translate('atLeastOneLowercase'), valid: requirements.hasLowercase },
        { label: translate('atLeastOneSpecialCharacter'), valid: requirements.hasSymbols },
    ];

    return (
        <View style={styles.container}>
            {showOldPassword ? (
                <PasswordInput
                    label={translate('oldPassword')}
                    placeholder=''
                    value={oldPassword}
                    onChangeText={(value: string) => onChangeOldPassword?.(value)}
                    invalid={() => false}
                    required={true}
                    viewStyle={inputStyle}
                />
            ) : null}
            <View style={styles.passwordInputContainer(stacked || windowInfo.isMobile)}>
                <PasswordInput
                    label={translate('newPassword')}
                    placeholder=''
                    value={password}
                    onChangeText={onChangePassword}
                    invalid={() => false}
                    required={true}
                    disabled={newPasswordsDisabled}
                    viewStyle={inputStyle}
                />
                <PasswordInput
                    label={translate('confirmNewPassword')}
                    placeholder=''
                    value={confirmPassword}
                    onChangeText={onChangeConfirmPassword}
                    invalid={() => password != confirmPassword}
                    errorMessage={translate('passwordsNotEqual')}
                    required={true}
                    disabled={newPasswordsDisabled}
                    viewStyle={inputStyle}
                />
            </View>
            <View style={{ gap: 4 }}>
                {requirementItems.map(item => (
                    <View key={item.label} style={styles.requirementContainer}>
                        <FontAwesomeIcon
                            icon={faCircleCheck as IconDefinition}
                            fontSize={16}
                            style={password === '' ? styles.waiting : item.valid ? styles.valid : styles.invalid}
                        />
                        <MyAppText style={[styles.label, password === '' ? null : item.valid ? styles.valid : styles.invalid]}>
                            {item.label}
                        </MyAppText>
                    </View>
                ))}
            </View>
            <WarningMessage
                messages={[
                    translate('passwordPersonalDataWarning'),
                    translate('passwordHistoryWarning'),
                ]}
            />
        </View>
    );
}

const styleSheet = createStyleSheet((theme) => ({
    container: {
        gap: 16,
    },
    passwordInputContainer: (isMobile: boolean) => ({
        gap: 16,
        flexDirection: isMobile ? 'column' : 'row'
    }),
    requirementContainer: {
        flexDirection: 'row',
        gap: 8,
        alignItems: 'center'
    },
    label: {
        fontSize: 14,
        fontWeight: '500',
        color: theme.colors.neutral['400'],
    },
    waiting: {
        color: theme.colors.neutral['400'],
    },
    valid: {
        color: theme.colors.successes['300'],
    },
    invalid: {
        color: theme.colors.errors['300'],
    }
}));
